import React, { useCallback, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'design-system/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import _ from 'lodash';
import { Box, Dialog, H3, makeStyles, Tooltip } from 'react-scale/components/design-system';
import { InfoBanner } from 'react-scale/corp/components/NiceConfigs/NiceConfigs';
import { useTimeout } from 'react-use-v13';
import { getLabelFor } from '@scale/shared/types/remoAdmin';
import useQueryParams from 'react-scale/corp/hooks/useQueryParams';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import { canCancelQAOperation } from '@scale/shared/qaOperation/stateMachineDefs/qaOperationTransitionGuards';
import { CancelOperationButton, QAOperationSubmitActions } from './QAOperationActions';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { CopyCell } from 'react-scale/catalog/components/DataGrid/Cells';
import { renderAttemptTimeSpent, renderDueDate } from './tables/helpers';
import { qaOperationHasEvent, useQAOperationContext } from './qaOperationContext';

const useStyles = makeStyles({
  operationHeaderLabel: {
    borderBottom: '2px solid var(--color-RemoGreen)',
    marginBottom: 2,
    marginTop: 4,
  },
  submitOperationButton: {
    margin: '1ex',
    display: 'flex',
    width: 'fit-content',
    float: 'right',
  },
  documentOperationWarning: {
    margin: '2ex',
    fontWeight: 'bolder',
  },
});

interface IQAOperationModalProps {
  isOpen?: boolean;
  attemptResult?: any;
  onClose?: () => void;
}

interface IQAOperationSubmitButton extends IQAOperationModalProps {
  disabled?: boolean;
}
const QAOperationHeaderAttribute: React.FC<{ label: string; children?: React.ReactNode }> = ({
  label,
  children,
}) => {
  const styles = useStyles();
  return (
    <Grid item>
      <Typography
        variant="subtitle2"
        component="div"
        fontWeight={'bold'}
        className={styles.operationHeaderLabel}
      >
        {label}:
      </Typography>
      <i>{children}</i>
    </Grid>
  );
};

export const QAOperationHeader: React.FC<
  IQAOperationSubmitButton & { overrideSubmitButton?: boolean }
> = props => {
  const { qaOperation, getCurrentState, getOperationID } = useQAOperationContext();
  return (
    <div style={{ width: '100%' }}>
      <Grid container spacing={2} flexDirection={'row-reverse'}>
        {props.overrideSubmitButton ? (
          <QAOperationSubmitModal {...props} />
        ) : (
          <Grid item>
            <QAOperationSubmitButton {...props} />
          </Grid>
        )}
        <QAOperationHeaderAttribute label={'Current State'}>
          {_.startCase(getCurrentState())}
        </QAOperationHeaderAttribute>
        <QAOperationHeaderAttribute label={'Time Spent / Max Time Needed'}>
          {renderAttemptTimeSpent({ value: { operation: qaOperation } } as any)}
        </QAOperationHeaderAttribute>
        <QAOperationHeaderAttribute label={'Due Date'}>
          {renderDueDate({ value: { operation: qaOperation } } as any)}
        </QAOperationHeaderAttribute>
        <QAOperationHeaderAttribute label={'Operation ID'}>
          <CopyCell
            name={getOperationID().slice(-5)}
            value={getOperationID().slice(-5)}
            copy={getOperationID()}
            tooltip={'Copy ID'}
          />
        </QAOperationHeaderAttribute>
        {qaOperation?.name && (
          <QAOperationHeaderAttribute label={'Operation'}>
            <Tooltip title={qaOperation?.name} placement="bottom">
              <span>
                {qaOperation?.name.slice(0, 40)}
                {(qaOperation?.name.length ?? 0) > 40 ? '...' : ''}
              </span>
            </Tooltip>
          </QAOperationHeaderAttribute>
        )}
      </Grid>
      <hr />
    </div>
  );
};

const QAOperationSubmitButton: React.FC<IQAOperationSubmitButton> = props => {
  const { qaOperation, isInReview } = useQAOperationContext();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const styles = useStyles();
  const onSubmitClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  if (!qaOperation) return <></>;

  return (
    <>
      <Button
        className={styles.submitOperationButton}
        variant="contained"
        color="primary"
        onClick={onSubmitClick}
      >
        Submit {_.startCase(qaOperation.type)} {isInReview() ? 'Review' : 'Assignment'}
      </Button>
      <QAOperationSubmitModal {...props} isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

const QAOperationSubmitModal = React.memo<IQAOperationModalProps>(function QAOperationModal({
  isOpen,
  onClose,
  ...rest
}) {
  const qaOperationContext = useQAOperationContext();
  const { qaOperation, documentType, userId } = qaOperationContext;
  const attemptResult = qaOperationContext.attemptResult || rest.attemptResult;
  const [message, setMessage] = useState('');
  const query = useQueryParams();
  const handleChangeMessage: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    e => setMessage(e.target.value),
    [],
  );

  const opType = getLabelFor(qaOperation?.type ?? '');
  const closeOnComplete = query.closeOnComplete?.toString() === '1';

  const handleSubmit = React.useCallback(() => {
    if (closeOnComplete) window.close();
    else if (onClose) onClose();
  }, [closeOnComplete, onClose]);

  const canCompleteReview = qaOperationHasEvent('completeReview', qaOperation);
  const canCompleteAttempt = qaOperationHasEvent('completeAttempt', qaOperation);

  if (!qaOperation) return <></>;

  return (
    <>
      <Modal
        isOpen={isOpen}
        contentLabel={`Complete Assigned ${opType} Operation`}
        onRequestClose={onClose}
        maxWidth="100%"
      >
        <ModalHeader>Submit Assigned {opType} Operation</ModalHeader>
        <ModalBody>
          <Box
            border={'1px solid var(--color-RemoGray20)'}
            style={{ marginTop: '1ex', padding: '2ex' }}
          >
            <Typography variant="body2">Submit the results of your {opType} operation.</Typography>
            {canCompleteAttempt && attemptResult && (
              <ReactJsonView
                collapsed={0}
                enableClipboard={false}
                displayDataTypes={false}
                name={documentType()}
                src={attemptResult}
              />
            )}
            {canCompleteReview && (
              <>
                <Typography variant="body2">Leave a review message for the attempter.</Typography>
                <TextField
                  value={message}
                  label="Message"
                  onChange={handleChangeMessage}
                  fullWidth={true}
                  variant="outlined"
                  multiline
                  rows={4}
                />
              </>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Stack spacing={1} direction="column">
            <QAOperationSubmitActions
              attemptResult={attemptResult}
              reviewMessage={message}
              onSubmit={handleSubmit}
              qaOperation={qaOperation}
              userId={userId}
            />
          </Stack>
        </ModalFooter>
      </Modal>

      <QADocumentOperationWarning />
    </>
  );
});

const QADocumentOperationWarning: React.FC = props => {
  const styles = useStyles();
  const { hidePII } = useRemoAdminCtx();
  const { qaOperation, userId, currentUserIsReviewer, currentUserIsAttempter, documentType } =
    useQAOperationContext();

  const [canDismiss] = useTimeout(5000);
  const canCancel = useMemo(() => {
    try {
      canCancelQAOperation(qaOperation!, userId, !hidePII);
      return true;
    } catch {
      return false;
    }
  }, [qaOperation, userId, hidePII]);

  if (currentUserIsReviewer() || currentUserIsAttempter() || !qaOperation) return <></>;

  return (
    <Dialog open={true} maxWidth="xs">
      <div className={styles.documentOperationWarning}>
        <H3>QA Operation Note ({qaOperation.type})</H3>
        <InfoBanner
          message={`This ${documentType()} is currently being worked on by a RemoAdmin QA/tasker as part of Operation ID ${
            qaOperation._id
          }. 
          Any changes made outside of that operation will not be saved. If you want to work on this ${documentType()}, please cancel the current operation.`}
        />
      </div>
      {canCancel && (
        <CancelOperationButton
          userId={userId}
          {...props}
          qaOperation={qaOperation}
          disabled={!canDismiss()}
        />
      )}
    </Dialog>
  );
};
